import imageCompression from "browser-image-compression";
import { Camera } from "lucide-react";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import LEKSelect from "src/components/Default/LekUI/Select";
import { TextField } from "src/components/Default/LekUI/TextField";
import { UploadModal } from "src/components/Modals/UploadModal";
import { getAppConfig } from "src/hooks/useAppConfig";
import { useSources } from "src/hooks/useComments";

export interface PhotoSource {
	id: string | number | null;
	source: string | null;
}

interface SourceOption extends Record<string, unknown> {
	id: string | number;
	source: string;
}

export interface UploadedPhoto {
	image: { file: Blob; path: string };
	source: PhotoSource | null;
}

const maxSourceLength = 300;

interface SelectSourceProps {
	sources: SourceOption[];
	value: PhotoSource | null;
	setValue: (value: PhotoSource) => void;
	error?: string;
}

const SelectSource = ({ sources, value, setValue, error }: SelectSourceProps) => {
	const { t } = useTranslation();
	const [isNewSource, setIsNewSource] = useState(false);

	return (
		<div className="flex flex-col gap-4 mt-32">
			{isNewSource ? (
				<TextField
					placeholder={t("sources.placeholderInput")}
					fullWidth
					size="small"
					error={error}
					onChange={(e) => setValue({ id: null, source: e.target.value })}
					value={value?.source ?? ""}
				/>
			) : (
				<LEKSelect
					title={t("sources.placeholderSelect")}
					value={value?.id ?? undefined}
					onChange={(e) =>
						setValue({ id: e.target.value as string | number, source: null })
					}
					options={sources}
					optionKey="source"
				/>
			)}
			<span className="size-14-light text-grey">
				<Trans
					i18nKey={
						isNewSource ? "sources.useExistingSources" : "sources.addNewSource"
					}
					components={[
						<span
							className="size-14-light link"
							onClick={() => setIsNewSource(!isNewSource)}
							key="source"
						/>,
					]}
				/>
			</span>
		</div>
	);
};

interface UploadPhotoModalProps {
	onClose: () => void;
	onConfirm: (photo: UploadedPhoto) => void;
}

export const UploadPhotoModal = ({ onClose, onConfirm }: UploadPhotoModalProps) => {
	const appConfig = getAppConfig();
	const { t } = useTranslation();
	const { data: sources = [] } = useSources() as { data?: SourceOption[] };
	const [source, setSource] = useState<PhotoSource | null>(null);

	useEffect(() => {
		if (sources.length > 0) {
			setSource({ id: sources[0].id, source: null });
		}
	}, [sources]);

	const sourceError =
		source?.source && source.source.length > maxSourceLength
			? t("errors.maxSourceLength", { maxLength: maxSourceLength })
			: undefined;

	const handleConfirm = async ({ file }: { file: Blob }) => {
		let compressedFile = file;
		if (file.size > appConfig.maxCompressionSizeMb * 1024 * 1024) {
			compressedFile = await imageCompression(file as File, {
				maxSizeMB: appConfig.maxCompressionSizeMb,
				useWebWorker: false,
			});
		}
		onConfirm({
			image: { file: compressedFile, path: URL.createObjectURL(compressedFile) },
			source,
		});
	};

	return (
		<UploadModal
			mode="photo"
			allowedFormats="jpg, png, svg"
			accept="image/jpeg,image/png,image/svg+xml"
			Icon={Camera}
			title={t("exam.comments.addImage")}
			confirmDisabled={!source || !!sourceError}
			onConfirm={handleConfirm}
			onClose={onClose}
		>
			{() => (
				<SelectSource
					value={source}
					setValue={setSource}
					sources={sources}
					error={sourceError}
				/>
			)}
		</UploadModal>
	);
};
